import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AdminProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const adminFeatures = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      action: () => navigate('/admin/users'),
      buttonText: 'Manage Users',
      available: user?.role === 'admin' || user?.role === 'super_admin'
    },
    {
      title: 'Profile Settings',
      description: 'Update your personal profile information',
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      action: () => navigate('/admin/profile'),
      buttonText: 'Edit Profile',
      available: true
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings',
      icon: <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      action: () => navigate('/admin/settings'),
      buttonText: 'System Settings',
      available: user?.role === 'super_admin'
    }
  ];

  const getRoleDescription = (role: string) => {
    const descriptions: { [key: string]: string } = {
      super_admin: 'Full system access with all permissions including user management and settings',
      admin: 'Administrative access with user management capabilities (except super admin users)',
      form_manager: 'Can create and edit forms, view submissions',
      payment_approver: 'Can view submissions and approve payments',
      submission_viewer: 'Can view submissions only',
      submission_editor: 'Can view and edit submissions',
      notification_manager: 'Can send SMS and email notifications',
      user: 'Regular user with basic access'
    };
    return descriptions[role] || 'Standard user permissions';
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Profile
        </Typography>
        
        {/* Current User Info */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Current User Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body1">
              <strong>Username:</strong> {user.username}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body1">
              <strong>Role:</strong> {user.role.replace('_', ' ').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              <strong>Status:</strong> {user.isActive ? 'Active' : 'Inactive'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Role Description:</strong> {getRoleDescription(user.role)}
            </Typography>
          </Box>
        </Box>

        {/* Admin Features */}
        <Typography variant="h6" gutterBottom>
          Available Admin Features
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box display="flex" flexWrap="wrap" gap={3}>
          {adminFeatures.filter(feature => feature.available).map((feature, index) => (
            <Card key={index} sx={{ minWidth: 300, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {feature.description}
                </Typography>
                <Button
                  variant="contained"
                  onClick={feature.action}
                  fullWidth
                >
                  {feature.buttonText}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {adminFeatures.filter(feature => feature.available).length === 1 && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Contact your system administrator for additional permissions or role changes.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AdminProfile;
